import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Content } from '../types';
import axiosInstance from '../api/axiosInstance';
import { getFileTypeInfo, formatFileSize, truncateFileName } from '../utils/fileUtils';
import { getCategoryPath } from '../utils/categoryUtils';
import { useAuth } from '../hooks/useAuth';
import { getTopicArea } from '../api/topicAreaApi';
import { getContents } from '../api/contentApi';
import { getDomainName } from '../utils/linkUtils';
import './ContentPage.css';

const ContentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canModifyContent } = useAuth();
  const [content, setContent] = useState<Content | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [topicArea, setTopicArea] = useState<any>(null);
  const [timelineNeighbors, setTimelineNeighbors] = useState<{
    previous: Content | null;
    next: Content | null;
  }>({
    previous: null,
    next: null
  });

  const fetchComments = useCallback(async () => {
    if (id) {
      try {
        const res = await axiosInstance.get(`/api/comments/${id}`);
        setComments(res.data);
      } catch (err) {
        console.error('댓글 로딩 실패:', err);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      axiosInstance.get(`/api/contents/${id}`).then(res => {
        const contentData = res.data as Content;
        setContent(contentData);

        const topicAreaId = contentData.topic_area_id || null;
        getContents(undefined, null, topicAreaId).then(items => {
          const orderedItems = [...items].sort((a, b) => {
            const timestampDiff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
            return timestampDiff || a.id - b.id;
          });
          const currentIndex = orderedItems.findIndex(item => String(item.id) === String(id));

          setTimelineNeighbors({
            previous: currentIndex > 0 ? orderedItems[currentIndex - 1] : null,
            next: currentIndex >= 0 && currentIndex < orderedItems.length - 1 ? orderedItems[currentIndex + 1] : null
          });
        }).catch(err => {
          console.error('타임라인 이웃 사건 로딩 실패:', err);
          setTimelineNeighbors({ previous: null, next: null });
        });

        if (topicAreaId) {
          getTopicArea(topicAreaId).then(setTopicArea);
        } else {
          setTopicArea(null);
        }
      });
      fetchComments();
      axiosInstance.get(`/api/attach/${id}`).then(res => setFiles(res.data));
      axiosInstance.get('/api/categories').then(res => setCategories(res.data)).catch(err => {
        console.error('카테고리 로딩 실패:', err);
      });
      axiosInstance.get(`/api/tags/content/${id}`).then(res => setTags(res.data)).catch(err => {
        console.error('태그 로딩 실패:', err);
      });
    }
  }, [id, fetchComments]);

  // 실시간 댓글 업데이트 수신
  useEffect(() => {
    const handleCommentChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { contentType, contentId } = customEvent.detail;
      if (contentType === 'archive' && String(contentId) === String(id)) {
        fetchComments();
      }
    };

    window.addEventListener('comment_change', handleCommentChange);
    return () => {
      window.removeEventListener('comment_change', handleCommentChange);
    };
  }, [id, fetchComments]);

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;
    
    try {
      const res = await axiosInstance.post('/api/comments', {
        content_id: id,
        user_id: user.id,
        comment: newComment
      });
      
      const commentsRes = await axiosInstance.get(`/api/comments/${id}`);
      setComments(commentsRes.data);
      setNewComment('');
    } catch (err: any) {
      if (err.response?.status === 401) {
        alert('로그인이 필요합니다.');
        navigate('/login');
      } else {
        alert('댓글 작성에 실패했습니다.');
      }
    }
  };

  const handleBackToList = () => {
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    if (returnTo) {
      navigate(returnTo, {
        replace: true,
        state: { restoreContentScroll: true }
      });
      return;
    }
    navigate('/recommended');
  };

  const handleEdit = () => {
    navigate(`/content/${id}/edit`);
  };

  const handleNeighborNavigate = (neighbor: Content | null) => {
    if (!neighbor) return;
    navigate(`/content/${neighbor.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async () => {
    if (!window.confirm('정말로 이 컨텐츠를 삭제하시겠습니까?\n연관된 댓글과 파일도 모두 삭제됩니다.')) return;

    try {
      await axiosInstance.delete(`/api/contents/${id}`);
      alert('컨텐츠가 삭제되었습니다.');
      navigate('/');
    } catch (err: any) {
      console.error('컨텐츠 삭제 실패:', err);
      alert(err.response?.data?.error || '컨텐츠 삭제에 실패했습니다.');
    }
  };

  const handleEditComment = (comment: any) => {
    setEditingCommentId(comment.id);
    setEditingComment(comment.comment);
  };

  const handleUpdateComment = async (commentId: number) => {
    try {
      await axiosInstance.put(`/api/comments/${commentId}`, {
        comment: editingComment
      });
      
      const commentsRes = await axiosInstance.get(`/api/comments/${id}`);
      setComments(commentsRes.data);
      setEditingCommentId(null);
      setEditingComment('');
    } catch (err) {
      alert('댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) return;

    try {
      await axiosInstance.delete(`/api/comments/${commentId}`);
      
      const commentsRes = await axiosInstance.get(`/api/comments/${id}`);
      setComments(commentsRes.data);
    } catch (err) {
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  const handleReply = async (commentId: number) => {
    if (!replyContent.trim() || !user) return;

    try {
      await axiosInstance.post('/api/comments', {
        content_id: id,
        user_id: user.id,
        parent_id: commentId,
        comment: replyContent.trim()
      });
      
      const commentsRes = await axiosInstance.get(`/api/comments/${id}`);
      setComments(commentsRes.data);
      setReplyingTo(null);
      setReplyContent('');
    } catch (err) {
      alert('답글 작성에 실패했습니다.');
    }
  };

  const toggleReplies = (commentId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const renderComment = (comment: any, depth: number = 0) => {
    const replies = comments.filter(c => c.parent_id === comment.id);
    const hasMultipleReplies = replies.length > 1;
    const isExpanded = expandedComments.has(comment.id);

    return (
      <div key={comment.id} className="comment-node-wrapper" style={{ marginLeft: depth * 24 }}>
        {depth > 0 && <div className="comment-indent-line" />}
        
        <div className="comment-card" style={{ marginBottom: 12 }}>
          <div className="comment-card-header">
            <div className="comment-user-info">
              {hasMultipleReplies && (
                <button
                  type="button"
                  onClick={() => toggleReplies(comment.id)}
                  className="comment-toggle-btn"
                >
                  <svg 
                    className={`comment-chevron-svg ${isExpanded ? 'expanded' : ''}`} 
                    viewBox="0 0 24 24" 
                    width="14" 
                    height="14"
                  >
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor" />
                  </svg>
                  <span>{replies.length}</span>
                </button>
              )}
              <span className="comment-author-name">
                {comment.display_name || '익명'}
              </span>
            </div>
            
            <div className="comment-meta-info">
              <span className="comment-created-time">
                {new Date(comment.created_at).toLocaleString()}
                {comment.updated_at && new Date(comment.updated_at) > new Date(comment.created_at) && (
                  <span style={{ marginLeft: 4, color: '#6c757d' }}>
                    (수정됨)
                  </span>
                )}
              </span>
            </div>
          </div>

          {editingCommentId === comment.id ? (
            <textarea
              value={editingComment}
              onChange={(e) => setEditingComment(e.target.value)}
              className="comment-textarea-box"
              style={{ minHeight: 80, marginBottom: 8 }}
            />
          ) : (
            <p className="comment-body-content">
              {comment.comment}
            </p>
          )}

          <div className="comment-actions-bar">
            {user && (
              <div>
                {replyingTo !== comment.id && (
                  <button
                    type="button"
                    onClick={() => setReplyingTo(comment.id)}
                    className="comment-action-btn-chip reply"
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 17 4 12 9 7"></polyline>
                      <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                    </svg>
                    <span>답글 작성</span>
                  </button>
                )}
              </div>
            )}

            {user && (user.id === comment.user_id || (user.roles && (user.roles.includes('ROLE_ADMIN') || user.roles.includes('admin')))) && (
              <div className="comment-author-actions">
                {editingCommentId === comment.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleUpdateComment(comment.id)}
                      className="comment-action-btn-chip save"
                    >
                      저장
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCommentId(null);
                        setEditingComment('');
                      }}
                      className="comment-action-btn-chip cancel"
                    >
                      취소
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleEditComment(comment)}
                      className="comment-action-btn-chip edit"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span>수정</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="comment-action-btn-chip delete"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      <span>삭제</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {replyingTo === comment.id && (
            <div className="reply-input-box">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="답글을 입력하세요..."
                className="comment-textarea-box"
                style={{ minHeight: 60, marginBottom: 8 }}
              />
              <div className="reply-actions-row">
                <button
                  type="button"
                  onClick={() => handleReply(comment.id)}
                  disabled={!replyContent.trim()}
                  className="comment-action-btn-chip save"
                  style={{ opacity: replyContent.trim() ? 1 : 0.5 }}
                >
                  답글 작성
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                  }}
                  className="comment-action-btn-chip cancel"
                >
                  취소
                </button>
              </div>
            </div>
          )}
        </div>
        {(isExpanded || !hasMultipleReplies) && replies.map(reply => renderComment(reply, depth + 1))}
      </div>
    );
  };

  if (!content) return <div style={{ padding: 40, textAlign: 'center', fontWeight: 'bold' }}>Loading...</div>;

  const canEdit = content ? canModifyContent(content.user_id) : false;

  // 카테고리 경로 생성
  const categoryPaths = [
    (content as any).main_category1_id ? getCategoryPath((content as any).main_category1_id, categories) : '',
    (content as any).main_category2_id ? getCategoryPath((content as any).main_category2_id, categories) : '',
    (content as any).main_category3_id ? getCategoryPath((content as any).main_category3_id, categories) : ''
  ].filter(Boolean);

  return (
    <div className="content-detail-container">
      {(timelineNeighbors.previous || timelineNeighbors.next) && (
        <div className="content-timeline-neighbor-nav" aria-label="타임라인 사건 이동">
          {timelineNeighbors.previous && (
            <button
              type="button"
              className="content-neighbor-nav-btn previous"
              onClick={() => handleNeighborNavigate(timelineNeighbors.previous)}
              title={`이전 사건: ${timelineNeighbors.previous.title}`}
              aria-label={`이전 사건: ${timelineNeighbors.previous.title}`}
            >
              &larr;
            </button>
          )}
          {timelineNeighbors.next && (
            <button
              type="button"
              className="content-neighbor-nav-btn next"
              onClick={() => handleNeighborNavigate(timelineNeighbors.next)}
              title={`다음 사건: ${timelineNeighbors.next.title}`}
              aria-label={`다음 사건: ${timelineNeighbors.next.title}`}
            >
              &rarr;
            </button>
          )}
        </div>
      )}

      <div className="content-detail-header">
        <button 
          onClick={handleBackToList}
          className="comment-action-btn-chip cancel"
          style={{ padding: '8px 16px', fontSize: '13px' }}
        >
          목록으로
        </button>
        {canEdit && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={handleEdit}
              className="comment-action-btn-chip save"
              style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: '#007bff', borderColor: '#007bff' }}
            >
              수정
            </button>
            <button 
              onClick={handleDelete}
              className="comment-action-btn-chip delete"
              style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
            >
              삭제
            </button>
          </div>
        )}
      </div>

      <h2 className="content-detail-title">{content.title}</h2>
      
      {topicArea && (
        <div className="content-topic-badge">
          📅 주제영역: <a href={`/topic-areas/${topicArea.id}`}>{topicArea.title}</a>
        </div>
      )}
      
      {/* 카테고리 표시 */}
      {categoryPaths.length > 0 && (
        <div className="content-metadata-group">
          {categoryPaths.map((path, index) => (
            <div key={index} className="category-path-item">
              📂 {path}
            </div>
          ))}
        </div>
      )}
      
      {/* 태그 표시 */}
      {tags.length > 0 && (
        <div className="tag-list-wrapper">
          {tags.map((tag: any) => (
            <span key={tag.id} className="tag-badge-chip">
              🏷️ {tag.name}
            </span>
          ))}
        </div>
      )}
      
      <p className="content-description-text">{content.description}</p>
      
      <div className="content-date-meta" style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px', backgroundColor: 'var(--background)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginTop: '20px' }}>
        <div>
          ⏱️ 사건발생: {new Date(content.timestamp).toLocaleString()}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
          <span>
            최초 등록: {new Date(content.created_at).toLocaleString()}
            {content.creator_username && ` (소유자: ${content.creator_nickname || content.creator_username})`}
          </span>
          <span>
            최종 수정: {new Date(content.updated_at).toLocaleString()}
            {content.updater_username && ` (수정자: ${content.updater_nickname || content.updater_username})`}
          </span>
        </div>
      </div>

      {content.lnks && (
        <div className="links-section">
          <div className="links-title">
            🌐 관련 링크
          </div>
          <div className="links-grid">
            {content.lnks.split(';').filter(Boolean).map((lnk: string, i: number) => {
              const displayDomain = getDomainName(lnk);
              return (
                <a 
                  key={i} 
                  href={lnk} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="external-link-chip"
                  title={lnk}
                >
                  🌐 {displayDomain}
                  <svg className="external-svg-icon" viewBox="0 0 24 24" width="12" height="12">
                    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor" />
                  </svg>
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="content-attach-section">
        <strong className="content-attach-title">📎 첨부파일</strong>
        <div className="content-attach-grid">
          {files.map(file => {
            const isLabel = !file.file_type?.includes('/') && !file.file_type?.includes('application');
            const fileInfo = isLabel 
              ? (() => {
                  const labelIconMap: Record<string, string> = {
                    'Word 문서': '📄',
                    'Excel 문서': '📊', 
                    'PowerPoint 문서': '📽️',
                    'PDF 문서': '📋',
                    '이미지': '🖼️',
                    '비디오': '🎬',
                    '오디오': '🎵',
                    '압축 파일': '📦',
                    '텍스트': '📝'
                  };
                  const icon = labelIconMap[file.file_type] || '📎';
                  const colorMap: Record<string, string> = {
                    'Word 문서': '#2b579a',
                    'Excel 문서': '#217346',
                    'PowerPoint 문서': '#d24726',
                    'PDF 문서': '#dc3545',
                    '이미지': '#6f42c1',
                    '비디오': '#fd7e14',
                    '오디오': '#20c997',
                    '압축 파일': '#6c757d',
                    '텍스트': '#495057'
                  };
                  const color = colorMap[file.file_type] || '#007bff';
                  return { icon, label: file.file_type, color };
                })()
              : getFileTypeInfo(file.file_name, file.file_type);

            const isImage = file.file_type === '이미지' || 
                           file.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

            return (
              <div key={file.id} style={{ marginBottom: 16 }}>
                {isImage ? (
                  <div className="image-attach-card">
                    <div style={{ 
                      position: 'relative',
                      paddingBottom: '60%',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={file.thumb_middle 
                          ? `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.thumb_middle}`
                          : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.file_url}`
                        }
                        alt={file.file_name}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.file_url}`, '_blank');
                        }}
                      />
                    </div>
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ 
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: 4,
                        fontSize: '13.5px'
                      }}>
                        {truncateFileName(file.file_name, 35)}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6c757d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>{fileInfo.label}</span>
                        <div>
                          {file.file_size && (
                            <span style={{ marginRight: 8 }}>{formatFileSize(file.file_size)}</span>
                          )}
                          <a 
                            href={`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.file_url}`}
                            download={file.file_name}
                            style={{ 
                              color: '#007bff',
                              textDecoration: 'none',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            다운로드
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: 8,
                    transition: 'all 0.2s ease'
                  }}>
                    <span style={{ 
                      fontSize: '24px',
                      marginRight: 12,
                      color: fileInfo.color 
                    }}>
                      {fileInfo.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '600',
                        color: '#2c3e50',
                        marginBottom: 4,
                        wordBreak: 'break-all',
                        fontSize: '13.5px'
                      }}>
                        {truncateFileName(file.file_name, 40)}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6c757d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span style={{
                          backgroundColor: fileInfo.color,
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: 3,
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          {fileInfo.label}
                        </span>
                        {file.file_size && (
                          <span>{formatFileSize(file.file_size)}</span>
                        )}
                      </div>
                    </div>
                    <a 
                      href={`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${file.file_url}`}
                      download={file.file_name}
                      style={{ 
                        color: '#007bff',
                        textDecoration: 'none',
                        fontSize: '12px',
                        marginLeft: 12,
                        padding: '4px 8px',
                        border: '1px solid #007bff',
                        borderRadius: 4,
                        transition: 'all 0.2s ease',
                        fontWeight: '600'
                      }}
                    >
                      다운로드
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 댓글 섹션 */}
      <div className="comments-section">
        <h3 className="comments-header-title">💬 댓글 ({comments.length}개)</h3>
        
        {/* 댓글 작성 폼 */}
        <form onSubmit={handleCommentSubmit} className="comment-form-container">
          <div style={{ marginBottom: 12 }}>
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={user ? "댓글을 작성해주세요..." : "로그인 후 댓글을 작성할 수 있습니다."}
              disabled={!user}
              className="comment-textarea-box"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={!user || !newComment.trim()}
              className="comment-action-btn-chip save"
              style={{
                padding: '8px 20px',
                fontSize: '14.5px',
                backgroundColor: user && newComment.trim() ? '#28a745' : '#6c757d',
                borderColor: user && newComment.trim() ? '#28a745' : '#6c757d',
                cursor: user && newComment.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              댓글 작성
            </button>
          </div>
        </form>

        {/* 댓글 목록 */}
        <div className="comments-list-container">
          {comments.filter(comment => !comment.parent_id).map(comment => renderComment(comment))}
          
          {comments.length === 0 && (
            <div className="no-comments-placeholder">
              첫 번째 댓글을 작성해보세요!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
